export type Pet = {
  id: string;
  name: string;
  species: string;
  age?: string;
  color?: string;
  breed?: string | null;
  sex?: string | null;
  birth_date?: string | null;
  microchip_id?: string | null;
  photo_url?: string | null;
  last_record_update?: string | null;
  record_count?: number;
  status: string;
};

export type PetInput = Pick<Pet, 'name' | 'species' | 'breed' | 'sex' | 'birth_date' | 'microchip_id' | 'photo_url'> & { status?: 'active' | 'inactive' };

export type AccountRole = 'SUPER_ADMIN' | 'ACCOUNT_OWNER' | 'USER';
export type OwnerProfile = { id: string; name: string; email: string; phone: string | null; profile_complete: boolean; role: AccountRole };
export type FacilityType = 'VETERINARY_CLINIC' | 'BOARDING_FACILITY' | 'TRAINING_FACILITY' | 'OTHER';
export type FacilityVeterinarian = { id?: string; name: string; email?: string | null; phone?: string | null; license_number?: string | null };
export type FacilityMember = { id: string; role: 'ACCOUNT_OWNER' | 'USER'; owner: { id: string; name: string; email: string } };
export type FacilityInvitation = { id: string; email: string; role: 'ACCOUNT_OWNER' | 'USER'; created_at: string };
export type Facility = {
  id: string; name: string; type: FacilityType; contact_name: string | null; email: string | null;
  phone: string | null; website: string | null; address_line1: string; address_line2: string | null;
  city: string; region: string; postal_code: string; active: boolean; owned: boolean; role?: 'ACCOUNT_OWNER' | 'USER';
  verified_at?: string | null;
  veterinarians: FacilityVeterinarian[]; memberships?: FacilityMember[]; invitations?: FacilityInvitation[];
};
export type AdminFacilitiesPage = { facilities: Facility[]; total: number; page: number; page_size: number };
export type FacilitySearchResult = Pick<Facility, 'id' | 'name' | 'type' | 'contact_name' | 'email' | 'phone' | 'website' | 'address_line1' | 'address_line2' | 'city' | 'region' | 'postal_code' | 'verified_at' | 'veterinarians'>;
export type MyFacility = Facility & { pets: Pick<Pet, 'id' | 'name'>[] };
export type AdminUser = { id: string; name: string; email: string; joined_at: string; pets_count: number; documents_count: number };
export type AdminPetTypeSummary = { species: string; number: number; most_recent_created_at: string | null };
export type CareMessage = {
  id: string;
  conversation_id: string;
  sender_owner_id: string;
  sender_role: 'OWNER' | 'FACILITY';
  body: string;
  email_sent_at: string | null;
  email_delivery_error: string | null;
  read_at: string | null;
  created_at: string;
  sender: { id: string; name: string };
};
export type CareConversation = {
  id: string;
  owner_id: string;
  pet_id: string;
  facility_id: string;
  sender_role: 'OWNER' | 'FACILITY';
  unread_count: number;
  owner: { id: string; name: string; email: string };
  pet: { id: string; name: string; species: string };
  facility: { id: string; name: string; email: string | null; city: string; region: string };
  messages: CareMessage[];
  updated_at: string;
};
export type EmailTemplate = { id: string; key: string; name: string; subject: string; text_body: string; html_body: string; updated_at: string; updated_by: string | null };
export type DashboardActivityItem = { id: string; kind: 'pet' | 'document' | 'facility' | 'visit'; title: string; meta: string; created_at: string };
export type ScheduledVisit = { id: string; facility_id: string; starts_at: string; notes: string | null; status: 'SCHEDULED' | 'CANCELLED' | 'COMPLETED'; facility: Pick<Facility, 'id' | 'name' | 'address_line1' | 'city' | 'region'>; pets: { pet: Pick<Pet, 'id' | 'name' | 'species'> }[] };
export type SystemNotification = { id: string; type: string; title: string; body: string; href: string | null; read_at: string | null; created_at: string };
export type DashboardSummary = { activity: DashboardActivityItem[]; upcoming_visits: ScheduledVisit[]; pet_count: number };
export type SharedRecordPagePet = { id: string; name: string };
export type SharedRecordPageSummary = {
  id: string;
  created_at: string;
  expires_at: string;
  recipient_email: string | null;
  facility: { id: string; name: string };
  pets: SharedRecordPagePet[];
};
export type CreatedSharedRecordPage = SharedRecordPageSummary & { token: string };
export type PublicSharedRecordPage = {
  owner: { name: string; email: string; phone: string | null };
  facility: { name: string; address: string; email: string | null; phone: string | null };
  expires_at: string;
  pets: {
    id: string;
    name: string;
    records: { id: string; title: string; record_type: string; date: string; view_url: string | null }[];
  }[];
};

export function getMyAccount(token: string) {
  return request<OwnerProfile>('/api/owners/me', token);
}

export function updateMyProfile(token: string, profile: { name: string; phone: string | null }) {
  return request<OwnerProfile>('/api/owners/me/profile', token, { method: 'PATCH', body: JSON.stringify(profile) });
}

export function getFacilities(token: string, superAdmin: boolean) {
  return request<Facility[]>(superAdmin ? '/api/admin/facilities' : '/api/owners/me/managed-facilities', token);
}

export function searchAdminFacilities(token: string, query: string, page: number, pageSize = 50, type: FacilityType | 'all' = 'all', verification: 'all' | 'verified' | 'unverified' = 'all') {
  const params = new URLSearchParams({ q: query, page: String(page), page_size: String(pageSize), type: type === 'all' ? '' : type, verification });
  return request<AdminFacilitiesPage>(`/api/admin/facilities?${params}`, token);
}

export function getAdminUsers(token: string) {
  return request<AdminUser[]>('/api/admin/users', token);
}

export function getAdminPetTypes(token: string) {
  return request<AdminPetTypeSummary[]>('/api/admin/pet-types', token);
}

export function getDashboardSummary(token: string) {
  return request<DashboardSummary>('/api/owners/me/dashboard-summary', token);
}

export function getSystemNotifications(token: string) {
  return request<SystemNotification[]>('/api/owners/me/system-notifications', token);
}

export function markSystemNotificationRead(token: string, notificationId: string) {
  return request<{ success: boolean }>(`/api/owners/me/system-notifications/${encodeURIComponent(notificationId)}/read`, token, { method: 'PATCH' });
}

export function markAllSystemNotificationsRead(token: string) {
  return request<{ success: boolean }>('/api/owners/me/system-notifications/read-all', token, { method: 'PATCH' });
}

export function createScheduledVisit(token: string, visit: { facility_id: string; pet_ids: string[]; starts_at: string; notes?: string }) {
  return request<ScheduledVisit>('/api/owners/me/scheduled-visits', token, { method: 'POST', body: JSON.stringify(visit) });
}

export function getCareConversations(token: string) {
  return request<CareConversation[]>('/api/owners/me/care-conversations', token);
}

export function startCareConversation(token: string, facilityId: string, petId: string) {
  return request<CareConversation>('/api/owners/me/care-conversations', token, { method: 'POST', body: JSON.stringify({ facility_id: facilityId, pet_id: petId }) });
}

export function sendCareMessage(token: string, conversationId: string, body: string) {
  return request<CareMessage>(`/api/owners/me/care-conversations/${encodeURIComponent(conversationId)}/messages`, token, { method: 'POST', body: JSON.stringify({ body }) });
}

export function markCareConversationRead(token: string, conversationId: string) {
  return request<{ success: boolean }>(`/api/owners/me/care-conversations/${encodeURIComponent(conversationId)}/read`, token, { method: 'PATCH' });
}

export function getEmailTemplates(token: string) {
  return request<{ templates: EmailTemplate[]; available: { key: string; name: string }[] }>('/api/admin/email-templates', token);
}

export function updateEmailTemplate(token: string, key: string, template: Pick<EmailTemplate, 'subject' | 'text_body' | 'html_body'>) {
  return request<EmailTemplate>(`/api/admin/email-templates/${encodeURIComponent(key)}`, token, { method: 'PUT', body: JSON.stringify(template) });
}

export function createFacility(token: string, facility: Omit<Facility, 'id' | 'active' | 'owned' | 'role' | 'memberships' | 'invitations' | 'veterinarians'> & { veterinarians: Omit<FacilityVeterinarian, 'id'>[]; owner_email?: string }) {
  return request<Facility>('/api/admin/facilities', token, { method: 'POST', body: JSON.stringify(facility) });
}

export type UpdateFacilityInput = Pick<Facility, 'name' | 'type' | 'contact_name' | 'email' | 'phone' | 'website' | 'address_line1' | 'address_line2' | 'city' | 'region' | 'postal_code'> & {
  veterinarians: Omit<FacilityVeterinarian, 'id'>[];
};

export function updateFacility(token: string, facilityId: string, facility: UpdateFacilityInput) {
  return request<Facility>(`/api/admin/facilities/${encodeURIComponent(facilityId)}`, token, { method: 'PATCH', body: JSON.stringify(facility) });
}

export function updateFacilityOwned(token: string, facilityId: string, owned: boolean) {
  return request<Facility>(`/api/admin/facilities/${encodeURIComponent(facilityId)}/owned`, token, { method: 'PATCH', body: JSON.stringify({ owned }) });
}

export function getFacilityMembers(token: string, facilityId: string) {
  return request<{ memberships: FacilityMember[]; invitations: FacilityInvitation[] }>(`/api/facilities/${encodeURIComponent(facilityId)}/members`, token);
}

export function addFacilityMember(token: string, facilityId: string, email: string) {
  return request<{ status: 'added' | 'invited' }>(`/api/facilities/${encodeURIComponent(facilityId)}/members`, token, { method: 'POST', body: JSON.stringify({ email }) });
}

export function updateFacilityVerification(token: string, facilityId: string, verified: boolean) {
  return request<Facility>(`/api/admin/facilities/${encodeURIComponent(facilityId)}/verification`, token, { method: 'PATCH', body: JSON.stringify({ verified }) });
}

export function getMyFacilities(token: string) {
  return request<MyFacility[]>('/api/owners/me/facilities', token);
}

export function searchFacilities(token: string, query: string) {
  return request<FacilitySearchResult[]>(`/api/owners/me/facility-search?q=${encodeURIComponent(query)}`, token);
}

export type GoogleFacilityMatchCandidate = {
  place_id: string;
  name: string;
  address: string;
  address_line1: string;
  city: string;
  region: string;
  postal_code: string;
};

export type GoogleFacilityMatch = {
  place_id: string;
  status: 'verified' | 'unverified';
};

export type GoogleCarePlace = {
  id: string;
  displayName?: { text?: string };
  formattedAddress?: string;
  addressComponents?: { longText?: string; types?: string[] }[];
  location?: { latitude?: number; longitude?: number };
  primaryTypeDisplayName?: { text?: string };
  googleMapsUri?: string;
  rating?: number;
  userRatingCount?: number;
  businessStatus?: string;
};

export function searchGoogleCarePlaces(token: string, requestData: {
  latitude: number;
  longitude: number;
  location_label: string;
  category: 'all' | 'veterinary' | 'boarding' | 'pet-care';
}) {
  return request<{ places: GoogleCarePlace[] }>('/api/owners/me/find-care-search', token, {
    method: 'POST',
    body: JSON.stringify(requestData),
  });
}

export function matchGoogleFacilities(token: string, places: GoogleFacilityMatchCandidate[]) {
  return request<{ matches: GoogleFacilityMatch[] }>('/api/owners/me/facility-matches', token, {
    method: 'POST',
    body: JSON.stringify({ places }),
  });
}

export type MyFacilityInput = Omit<Facility, 'id' | 'active' | 'owned' | 'role' | 'verified_at' | 'memberships' | 'invitations' | 'veterinarians'> & {
  veterinarians: Omit<FacilityVeterinarian, 'id'>[];
  pet_ids: string[];
};

export function createMyFacility(token: string, facility: MyFacilityInput) {
  return request<MyFacility>('/api/owners/me/facilities', token, { method: 'POST', body: JSON.stringify(facility) });
}

export function addExistingMyFacility(token: string, facilityId: string, petIds: string[]) {
  return request<MyFacility>(`/api/owners/me/facilities/${encodeURIComponent(facilityId)}/uses`, token, {
    method: 'POST',
    body: JSON.stringify({ pet_ids: petIds }),
  });
}

export function removeMyFacility(token: string, facilityId: string) {
  return request<{ id: string; removed: boolean }>(`/api/owners/me/facilities/${encodeURIComponent(facilityId)}`, token, { method: 'DELETE' });
}

export function createSharedRecordPage(token: string, facilityId: string, petIds: string[], durationDays: number, recipientEmail: string) {
  return request<CreatedSharedRecordPage>('/api/owners/me/shared-record-pages', token, {
    method: 'POST',
    body: JSON.stringify({ facility_id: facilityId, pet_ids: petIds, duration_days: durationDays, recipient_email: recipientEmail || null }),
  });
}

export function getSharedRecordPages(token: string, facilityId: string) {
  return request<SharedRecordPageSummary[]>(`/api/owners/me/facilities/${encodeURIComponent(facilityId)}/shared-record-pages`, token);
}

export function revokeSharedRecordPage(token: string, pageId: string) {
  return request<{ id: string; revoked: boolean }>(`/api/owners/me/shared-record-pages/${encodeURIComponent(pageId)}`, token, { method: 'DELETE' });
}

export function sendSharedRecordPageEmail(token: string, pageId: string, shareToken: string, recipients: string[], testMode = false) {
  return request<{ sent: boolean; recipients: string[] }>(`/api/owners/me/shared-record-pages/${encodeURIComponent(pageId)}/send`, token, {
    method: 'POST',
    body: JSON.stringify({ token: shareToken, recipients, test_mode: testMode }),
  });
}

export type PetRecord = {
  id: string;
  pet_id: string;
  record_type: string;
  title: string;
  date: string;
  description: string | null;
  provider_name: string | null;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  has_file: boolean;
  created_at: string;
  updated_at: string;
};

export type PetRecordInput = Pick<PetRecord, 'record_type' | 'title' | 'date' | 'description' | 'provider_name' | 'file_name' | 'mime_type' | 'size_bytes'>;

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

async function request<T>(path: string, token: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null) as { message?: unknown } | null;
    throw new Error(typeof errorBody?.message === 'string' ? errorBody.message : `API request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

async function requestPublic<T>(path: string): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`);
  if (!response.ok) {
    const errorBody = await response.json().catch(() => null) as { message?: unknown } | null;
    throw new Error(typeof errorBody?.message === 'string' ? errorBody.message : `API request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function getPets(token: string) {
  return request<Pet[]>('/api/owners/me/pets', token);
}

export function createPet(token: string, pet: PetInput) {
  return request<Pet>('/api/owners/me/pets', token, {
    method: 'POST',
    body: JSON.stringify(pet),
  });
}

export function updatePet(token: string, petId: string, pet: PetInput) {
  return request<Pet>(`/api/owners/me/pets/${encodeURIComponent(petId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify(pet),
  });
}

export function getPetRecords(token: string, petId: string) {
  return request<{ records: PetRecord[]; storage_configured: boolean }>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records`, token);
}

export function createPetRecordUploadUrl(token: string, petId: string, file: File) {
  return request<{ upload_url: string; file_key: string }>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records/upload-url`, token, {
    method: 'POST',
    body: JSON.stringify({ file_name: file.name, mime_type: file.type, size_bytes: file.size }),
  });
}

export async function uploadPetRecordFile(uploadUrl: string, file: File) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Document upload failed (${response.status})`);
}

export function createPetRecord(token: string, petId: string, input: PetRecordInput & { file_key: string }) {
  return request<PetRecord>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updatePetRecord(token: string, petId: string, recordId: string, input: PetRecordInput & { file_key?: string }) {
  return request<PetRecord>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records/${encodeURIComponent(recordId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deletePetRecord(token: string, petId: string, recordId: string) {
  return request<{ id: string; removed: boolean }>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records/${encodeURIComponent(recordId)}`, token, { method: 'DELETE' });
}

export function getPetRecordViewUrl(token: string, petId: string, recordId: string) {
  return request<{ view_url: string }>(`/api/owners/me/pets/${encodeURIComponent(petId)}/records/${encodeURIComponent(recordId)}/view-url`, token);
}

export function getPublicSharedRecordPage(token: string) {
  return requestPublic<PublicSharedRecordPage>(`/api/public/records/${encodeURIComponent(token)}`);
}

export function archivePet(token: string, petId: string) {
  return request<{ id: string; status: string }>(`/api/owners/me/pets/${encodeURIComponent(petId)}`, token, {
    method: 'DELETE',
  });
}
